import React, { useState } from 'react';
import { Mail, ChevronRight, Phone, MessageSquare, Instagram, Youtube } from 'lucide-react';
import { categories } from '@/data/articles';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

// Google Form submission helper function for newsletter
const submitNewsletterToGoogleForm = async (email: string) => {
  // Google Form field IDs from the URL
  const formUrl = "https://docs.google.com/forms/d/e/1FAIpQLSfHtP98026SpH01PkPe3oIvA1Zkfh_iGI4mRp3L8rZ1mNMppA/formResponse";
  
  // Create form data object
  const formData = new FormData();
  formData.append("entry.28031474", email);
  
  // Using a no-CORS approach to submit the form
  return fetch(formUrl, {
    method: "POST",
    mode: "no-cors",
    body: formData
  });
};

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const handleScrollReset = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address to subscribe.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to Google Form
      await submitNewsletterToGoogleForm(email);
      
      console.log('Newsletter footer subscription submitted for:', email);
      
      toast({
        title: "Subscription Successful",
        description: "Thank you for subscribing to Manamu Media newsletter!",
        duration: 5000,
      });
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Subscription Failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white pt-16 pb-8 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Logo and intro section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-4">
            <div className="flex items-center mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-2 rounded-lg">
                <img 
                  src="/Manamu Media Logo.png" 
                  alt="Manamu Media Logo" 
                  className="h-12 w-auto brightness-125"
                />
              </div>
              <div className="ml-4 border-l border-gray-700 pl-4">
                <h2 className="text-2xl font-bold font-montserrat bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-purple-400 to-purple-500">
                  Manamu Media
                </h2>
              </div>
            </div>
            <p className="text-gray-300 mb-8 font-poppins text-sm leading-relaxed max-w-md">
              A premier business media platform delivering timely insights and analysis on markets, technology, finance, and global business development with unparalleled depth and perspective.
            </p>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-purple-300 flex-shrink-0" />
                <p className="text-gray-300 text-sm">+91 93475 37540</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-purple-300 flex-shrink-0" />
                <p className="text-gray-300 text-sm">contact@manamumedia.in</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <MessageSquare className="h-5 w-5 text-purple-300 flex-shrink-0" />
                <a 
                  href="https://whatsapp.com/channel/0029Vak8HQZGU3BRAbCQQg3e" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 text-sm hover:text-purple-300 transition-colors"
                >
                  Join Our WhatsApp Community
                </a>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <h3 className="text-xl font-bold mb-6 text-white font-montserrat">
              Categories
            </h3>
            <ul className="space-y-3">
              {categories.slice(0, 6).map((category) => (
                <li key={category.id}>
                  <a 
                    href={`#${category.id}`} 
                    className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                  >
                    <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                    {category.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="lg:col-span-2">
            <h3 className="text-xl font-bold mb-6 text-white font-montserrat">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="/" 
                  onClick={handleScrollReset}
                  className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                  Home
                </a>
              </li>
              <li>
                <a 
                  href="/success-story" 
                  onClick={handleScrollReset}
                  className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                  Success Stories
                </a>
              </li>
              <li>
                <a 
                  href="/achievers" 
                  onClick={handleScrollReset}
                  className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                  Achievers
                </a>
              </li>
              <li>
                <a 
                  href="/contact" 
                  onClick={handleScrollReset}
                  className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                  Contact
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/+919347537540?text=Hi%2C%20I%20want%20to%20list%20my%20story%20on%20Manamu%20Media" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                  Share Your Story
                </a>
              </li>
              <li>
                <a 
                  href="https://whatsapp.com/channel/0029Vak8HQZGU3BRAbCQQg3e" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white flex items-center transition-all duration-200 group text-sm"
                >
                  <ChevronRight className="h-3.5 w-3.5 mr-2 text-purple-400 group-hover:translate-x-1 transition-transform duration-200" />
                  Join Community
                </a>
              </li>
            </ul>
          </div>
          
          <div className="lg:col-span-4">
            <h3 className="text-xl font-bold mb-6 text-white font-montserrat">
              Subscribe to Our Newsletter
            </h3>
            <p className="text-gray-300 mb-4 text-sm">
              Get exclusive business insights delivered directly to your inbox.
            </p>
            
            <form onSubmit={handleSubscribe} className="relative mb-6">
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 pr-12 rounded-lg bg-gray-800/50 border border-gray-700 focus:border-purple-400 transition-colors duration-200 text-white focus:outline-none backdrop-blur-sm"
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="absolute right-1.5 top-1.5 bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 transition-all duration-200"
                size="sm"
              >
                {isSubmitting ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
              </Button>
            </form>
            
            <div className="flex space-x-4">
              <a href="https://www.instagram.com/manamumedia/" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 p-2.5 rounded-full transition-colors duration-200 group">
                <Instagram className="h-5 w-5 text-gray-400 group-hover:text-purple-400 transition-colors duration-200" />
              </a>
              <a href="https://www.youtube.com/@Manamumedia" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 p-2.5 rounded-full transition-colors duration-200 group">
                <Youtube className="h-5 w-5 text-gray-400 group-hover:text-purple-400 transition-colors duration-200" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-8"></div>
        
        {/* Bottom section */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
          <p className="mb-4 md:mb-0 font-montserrat">
            © {currentYear} Manamu Media. All rights reserved.
          </p>
          
          <div className="flex space-x-6">
            <a href="#" className="hover:text-purple-300 transition-colors duration-200">
              Privacy
            </a>
            <a href="#" className="hover:text-purple-300 transition-colors duration-200">
              Terms
            </a>
            <a href="#" className="hover:text-purple-300 transition-colors duration-200">
              Cookies
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
